import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import GroupPicker from '../components/GroupPicker';

export default function DocumentCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', description: '', keywords: '', folder_id: '', department_id: '',
    category_id: '', process_id: '', validity_type: 'none', validity_value: '',
    is_restricted: false, allow_printing: false, access_groups: [],
  });
  const [file, setFile] = useState(null);
  const [folders, setFolders] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/folders'), api.get('/departments'), api.get('/categories'), api.get('/processes'),
    ]).then(([f, d, c, p]) => {
      setFolders(f.data.data); setDepartments(d.data.data);
      setCategories(c.data.data); setProcesses(p.data.data);
    });
  }, []);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!file) { setError('Arquivo obrigatório'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        formData.append(key, key === 'access_groups' ? JSON.stringify(val) : val);
      });
      formData.append('file', file);
      const { data } = await api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      navigate(`/documents/${data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar documento');
    }
    setLoading(false);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold tracking-tight">Novo Documento</h1>
      </div>

      <div className="bg-surface-100 border rounded-md p-6 max-w-[700px]">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="p-3 rounded-md text-sm mb-4 bg-status-rejected/10 text-status-rejected border border-status-rejected/20">{error}</div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Nome *</label>
            <input className="form-input" name="name" value={form.name} onChange={handleChange} required />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Descrição</label>
            <textarea className="form-textarea" name="description" value={form.description} onChange={handleChange} />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Palavras-chave (separadas por virgula)</label>
            <input className="form-input" name="keywords" value={form.keywords} onChange={handleChange} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Pasta *', name: 'folder_id', options: folders, display: (f) => f.name },
              { label: 'Departamento *', name: 'department_id', options: departments, display: (d) => `${d.name} (${d.code})` },
              { label: 'Categoria *', name: 'category_id', options: categories, display: (c) => `${c.name} (${c.code})` },
              { label: 'Processo *', name: 'process_id', options: processes, display: (p) => `${p.name} (${p.code})` },
            ].map((field) => (
              <div className="mb-4" key={field.name}>
                <label className="block text-sm font-medium text-ink-secondary mb-1">{field.label}</label>
                <select className="form-select" name={field.name} value={form[field.name]} onChange={handleChange} required>
                  <option value="">Selecione...</option>
                  {field.options.map((o) => <option key={o.id} value={o.id}>{field.display(o)}</option>)}
                </select>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-secondary mb-1">Tipo de Validade</label>
              <select className="form-select" name="validity_type" value={form.validity_type} onChange={handleChange}>
                <option value="none">Sem validade</option>
                <option value="months">Meses</option>
                <option value="years">Anos</option>
              </select>
            </div>
            {form.validity_type !== 'none' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-ink-secondary mb-1">Valor</label>
                <input className="form-input" type="number" name="validity_value" value={form.validity_value} onChange={handleChange} min="1" />
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Arquivo *</label>
            <input type="file" className="form-input" onChange={(e) => setFile(e.target.files[0])} required />
          </div>

          <div className="flex gap-4 mb-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="is_restricted" checked={form.is_restricted} onChange={handleChange} />
              Acesso Restrito
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="allow_printing" checked={form.allow_printing} onChange={handleChange} />
              Permitir Impressão
            </label>
          </div>

          {form.is_restricted && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-secondary mb-1">Grupos de Acesso</label>
              <GroupPicker
                value={form.access_groups}
                onChange={(access_groups) => setForm((prev) => ({ ...prev, access_groups }))}
              />
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Criando...' : 'Criar Documento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
